import { UploadFile, UploadConfig, UploadResult } from '../types/upload.types';
import { UploadError, UploadErrorType, RetryHandler } from '../utils/error-handler';

/**
 * Upload options for individual file uploads
 */
export interface FileUploadOptions {
  headers?: Record<string, string>;
  timeout?: number;
  abortController?: AbortController;
  onProgress?: (progress: number) => void;
}

/**
 * UploadService - Handles file upload operations
 * 
 * Manages file uploads with progress tracking, pause/resume/cancel functionality,
 * retry logic, and comprehensive error handling.
 */
export class UploadService {
  private retryHandler: RetryHandler;
  private defaultTimeout = 300000; // 5 minutes without upload progress

  constructor(config?: UploadConfig) {
    this.retryHandler = new RetryHandler(config?.retries);
    if (config?.timeout !== undefined) {
      this.defaultTimeout = config.timeout;
    }
  }

  /**
   * Upload a single file with comprehensive error handling and retry logic
   */
  async uploadFile(
    file: UploadFile | File,
    url: string,
    options: FileUploadOptions = {}
  ): Promise<UploadResult> {
    const startTime = Date.now();
    const fileName = file.name;
    let retries = 0;

    const uploadOperation = async (): Promise<UploadResult> => {
      const controller = new AbortController();
      const timeout = options.timeout ?? this.defaultTimeout;
      let timedOut = false;
      let externallyAborted = false;
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      const resetTimeout = () => {
        if (timeout <= 0 || externallyAborted) return;
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          if (externallyAborted) return;
          timedOut = true;
          controller.abort();
        }, timeout);
      };
      const externalAbortHandler = () => {
        externallyAborted = true;
        if (timeoutId) clearTimeout(timeoutId);
        controller.abort();
      };

      try {
        if (options.abortController?.signal.aborted) {
          controller.abort();
        } else {
          options.abortController?.signal.addEventListener(
            'abort',
            externalAbortHandler,
            { once: true }
          );
        }
        resetTimeout();

        await this.performUpload(file, url, {
          ...options,
          abortController: controller,
          onProgress: options.onProgress
            ? (progress) => {
                resetTimeout();
                options.onProgress?.(progress);
              }
            : undefined,
        });

        return {
          fileName,
          success: true,
          retries,
          uploadTime: Date.now() - startTime,
          size: file.size,
        };

      } catch (error) {
        if (timedOut && !externallyAborted) {
          retries++;
          throw new UploadError(
            'Upload timed out',
            UploadErrorType.TIMEOUT_ERROR,
            true,
            undefined,
            error instanceof Error ? error : undefined
          );
        }
        const uploadError = UploadError.fromError(error);
        
        // Increment retry count for retryable errors
        if (uploadError.retryable) {
          retries++;
        }

        // Re-throw to be handled by retry handler
        throw uploadError;
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
        options.abortController?.signal.removeEventListener(
          'abort',
          externalAbortHandler
        );
      }
    };

    try {
      return await this.retryHandler.execute(uploadOperation, `Upload ${fileName}`);
    } catch (error) {
      const uploadError = error as UploadError;
      return {
        fileName,
        success: false,
        error: uploadError.message,
        retries,
        uploadTime: Date.now() - startTime,
        size: file.size,
      };
    }
  }

  /**
   * Perform the actual file upload using XMLHttpRequest or fetch
   */
  private async performUpload(
    file: UploadFile | File,
    url: string,
    options: FileUploadOptions
  ): Promise<void> {
    const { headers, onProgress, abortController } = options;

    // Use XMLHttpRequest for progress tracking, fetch otherwise
    if (onProgress) {
      return this.uploadWithXHR(file, url, headers, onProgress, abortController);
    } else {
      return this.uploadWithFetch(file, url, headers, abortController);
    }
  }

  /**
   * Upload using XMLHttpRequest for progress tracking
   */
  private uploadWithXHR(
    file: UploadFile | File,
    url: string,
    headers?: Record<string, string>,
    onProgress?: (progress: number) => void,
    abortController?: AbortController
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Set up abort handling
      if (abortController) {
        abortController.signal.addEventListener('abort', () => {
          xhr.abort();
        }, { once: true });
      }

      xhr.open('PUT', url);

      // Set headers
      if (headers) {
        Object.entries(headers).forEach(([key, value]) => {
          xhr.setRequestHeader(key, value);
        });
      }

      // Set default content type if not provided
      if (!headers?.['Content-Type']) {
        xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new UploadError(
            `HTTP Error: ${xhr.status} ${xhr.statusText}`,
            this.getErrorTypeFromStatus(xhr.status),
            xhr.status >= 500,
            xhr.status
          ));
        }
      };

      xhr.onerror = () => {
        reject(new UploadError(
          'Network error occurred',
          UploadErrorType.NETWORK_ERROR,
          true
        ));
      };

      xhr.ontimeout = () => {
        reject(new UploadError(
          'Upload timed out',
          UploadErrorType.TIMEOUT_ERROR,
          true
        ));
      };

      xhr.onabort = () => {
        reject(new UploadError(
          'Upload was aborted',
          UploadErrorType.ABORT_ERROR,
          false
        ));
      };

      // Track upload progress
      if (onProgress && xhr.upload) {
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100);
            onProgress(progress);
          }
        };
      }

      // Send the file
      if (abortController?.signal.aborted) {
        reject(new UploadError(
          'Upload was aborted',
          UploadErrorType.ABORT_ERROR,
          false
        ));
        return;
      }
      xhr.send('data' in file ? file.data : file);
    });
  }

  /**
   * Upload using fetch API
   */
  private async uploadWithFetch(
    file: UploadFile | File,
    url: string,
    headers?: Record<string, string>,
    abortController?: AbortController
  ): Promise<void> {
    const requestHeaders = {
      'Content-Type': file.type || 'application/octet-stream',
      ...headers,
    };

    const response = await fetch(url, {
      method: 'PUT',
      body: 'data' in file ? file.data : file,
      headers: requestHeaders,
      signal: abortController?.signal,
    });

    if (!response.ok) {
      throw new UploadError(
        `HTTP Error: ${response.status} ${response.statusText}`,
        this.getErrorTypeFromStatus(response.status),
        response.status >= 500,
        response.status
      );
    }
  }

  /**
   * Create an AbortController for upload cancellation
   */
  createAbortController(): AbortController {
    return new AbortController();
  }

  /**
   * Upload multiple files in parallel with concurrency control
   */
  async uploadFiles(
    files: (UploadFile | File)[],
    urls: string[],
    options: FileUploadOptions & { maxConcurrency?: number } = {}
  ): Promise<UploadResult[]> {
    if (files.length !== urls.length) {
      throw new Error('Files and URLs arrays must have the same length');
    }

    const { maxConcurrency = 3, ...uploadOptions } = options;
    const results: UploadResult[] = [];
    const promises: Promise<void>[] = [];

    let index = 0;

    const uploadNext = async (): Promise<void> => {
      while (index < files.length) {
        const currentIndex = index++;
        const file = files[currentIndex];
        const url = urls[currentIndex];

        try {
          const result = await this.uploadFile(file, url, uploadOptions);
          results[currentIndex] = result;
        } catch (error) {
          results[currentIndex] = {
            fileName: file.name,
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error',
            retries: 0,
            uploadTime: 0,
            size: file.size,
          };
        }
      }
    };

    // Start concurrent uploads
    for (let i = 0; i < Math.min(maxConcurrency, files.length); i++) {
      promises.push(uploadNext());
    }

    await Promise.all(promises);
    return results;
  }

  /**
   * Calculate upload speed in bytes per second
   */
  calculateUploadSpeed(bytesUploaded: number, elapsedTimeMs: number): number {
    if (elapsedTimeMs === 0) return 0;
    return Math.round((bytesUploaded * 1000) / elapsedTimeMs);
  }

  /**
   * Estimate remaining time in seconds
   */
  estimateRemainingTime(
    bytesUploaded: number,
    totalBytes: number,
    uploadSpeed: number
  ): number {
    if (uploadSpeed === 0 || bytesUploaded >= totalBytes) return 0;
    
    const remainingBytes = totalBytes - bytesUploaded;
    return Math.round(remainingBytes / uploadSpeed);
  }

  /**
   * Get error type from HTTP status code
   */
  private getErrorTypeFromStatus(status: number): UploadErrorType {
    if (status >= 500) {
      return UploadErrorType.SERVER_ERROR;
    }
    if (status === 403 || status === 401) {
      return UploadErrorType.PERMISSION_ERROR;
    }
    if (status === 413) {
      return UploadErrorType.QUOTA_ERROR;
    }
    if (status === 408) {
      return UploadErrorType.TIMEOUT_ERROR;
    }
    return UploadErrorType.VALIDATION_ERROR;
  }
}
